import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useState } from 'react';

/**
 * Seletor de período do desktop (dashboards): chips Hoje/7 dias/30 dias/Mês/Ano
 * + campo único com calendário em popover + botão atualizar. Extraído de
 * `SaleDashboard.js` sem mudança de comportamento.
 */
// Atalhos de período do desktop. Diferente do mobile (`periodRange` do kit),
// "Mês" e "Ano" vão até hoje (não até o fim do mês/ano).
const DESKTOP_PERIOD_OPTIONS = [
    { id: 'today', label: 'Hoje' },
    { id: '7d', label: '7 dias' },
    { id: '30d', label: '30 dias' },
    { id: 'month', label: 'Mês' },
    { id: 'year', label: 'Ano' },
];

function desktopPeriodRange(id) {
    const now = dayjs();
    const end = now.endOf('day');
    switch (id) {
        case '7d': return { start: now.subtract(6, 'day').startOf('day'), end };
        case '30d': return { start: now.subtract(29, 'day').startOf('day'), end };
        case 'month': return { start: now.startOf('month'), end };
        case 'year': return { start: now.startOf('year'), end };
        default: return { start: now.startOf('day'), end };
    }
}

// Chip ativo somente quando o período coincide exatamente (por dia) com o atalho.
function detectDesktopPeriod(start, end) {
    if (!start?.isValid?.() || !end?.isValid?.()) return null;
    const found = DESKTOP_PERIOD_OPTIONS.find((o) => {
        const r = desktopPeriodRange(o.id);
        return r.start.isSame(start, 'day') && r.end.isSame(end, 'day');
    });
    return found ? found.id : null;
}

const periodChipSx = (active) => ({
    height: 32,
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: 700,
    border: '1px solid',
    borderColor: active ? 'primary.main' : 'divider',
    bgcolor: active ? 'info.bg' : 'background.subtle',
    color: active ? 'primary.main' : 'text.secondary',
    '&:hover': { borderColor: 'primary.main', bgcolor: active ? 'info.bg' : 'background.subtle' },
});

// Dia do calendário com realce do intervalo (início/fim em destaque, dias
// intermediários com fundo suave). As props extras vêm de `slotProps.day`.
// `hoverDay`/`onHover` (só no modo `dualMonth`) pré-visualizam o intervalo enquanto
// o usuário escolhe a data final.
function RangeDay({ rangeStart, rangeEnd, hoverDay, onHover, day, ...other }) {
    const previewEnd = !rangeEnd && hoverDay && rangeStart && hoverDay.isAfter(rangeStart, 'day') ? hoverDay : null;
    const effectiveEnd = rangeEnd || previewEnd;
    const isEdge = (rangeStart && day.isSame(rangeStart, 'day')) || (rangeEnd && day.isSame(rangeEnd, 'day'));
    const inRange = rangeStart && effectiveEnd && day.isAfter(rangeStart, 'day')
        && (previewEnd ? !day.isAfter(effectiveEnd, 'day') : day.isBefore(effectiveEnd, 'day'));
    return (
        <PickersDay
            {...other}
            onMouseEnter={onHover ? () => onHover(day) : other.onMouseEnter}
            day={day}
            selected={Boolean(isEdge)}
            sx={inRange && !other.outsideCurrentMonth ? { bgcolor: 'info.bg', borderRadius: '50%' } : undefined}
        />
    );
}

// Campo único de período (40px, `tb-field`): abre um calendário em popover
// onde o 1º clique define o início e o 2º define o fim. Ao concluir, aplica.
const NoCalendarHeader = () => null;

const monthTitleSx = { flex: 1, textAlign: 'center', fontSize: '14px', fontWeight: 700, textTransform: 'capitalize' };

function PeriodRangeField({ start, end, onApply, dualMonth = false, clearable = false }) {
    const wide = useMediaQuery('(min-width:600px)');
    const dual = dualMonth && wide;
    // Meses exibidos no modo `dualMonth`; navegam de forma independente (esquerdo < direito).
    const [leftMonth, setLeftMonth] = useState(() => dayjs().startOf('month'));
    const [rightMonth, setRightMonth] = useState(() => dayjs().startOf('month').add(1, 'month'));
    const [hoverDay, setHoverDay] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [draftStart, setDraftStart] = useState(null);
    const [draftEnd, setDraftEnd] = useState(null);
    const [picking, setPicking] = useState(false);

    const label = start?.isValid?.() && end?.isValid?.()
        ? `${start.format('DD/MM/YYYY')} – ${end.format('DD/MM/YYYY')}`
        : 'Selecionar período';

    const handleOpen = (event) => {
        setDraftStart(start);
        setDraftEnd(end);
        setPicking(false);
        setHoverDay(null);
        const left = (start?.isValid?.() ? start : dayjs()).startOf('month');
        const endMonth = end?.isValid?.() ? end.startOf('month') : null;
        setLeftMonth(left);
        setRightMonth(endMonth && endMonth.isAfter(left, 'month') ? endMonth : left.add(1, 'month'));
        setAnchorEl(event.currentTarget);
    };

    // Navegação independente; o outro painel só é empurrado para manter esquerdo < direito.
    const moveLeft = (delta) => {
        const next = leftMonth.add(delta, 'month');
        setLeftMonth(next);
        if (!rightMonth.isAfter(next, 'month')) setRightMonth(next.add(1, 'month'));
    };
    const moveRight = (delta) => {
        const next = rightMonth.add(delta, 'month');
        setRightMonth(next);
        if (!next.isAfter(leftMonth, 'month')) setLeftMonth(next.subtract(1, 'month'));
    };
    const handleClose = () => setAnchorEl(null);

    const hasPeriod = Boolean(start?.isValid?.() && end?.isValid?.());
    const handleClear = () => {
        setDraftStart(null);
        setDraftEnd(null);
        setPicking(false);
        onApply(null, null);
        handleClose();
    };

    const handlePick = (day) => {
        if (!day) return;
        if (!picking || day.isBefore(draftStart, 'day')) {
            setDraftStart(day);
            setDraftEnd(null);
            setPicking(true);
            return;
        }
        setDraftEnd(day);
        setPicking(false);
        onApply(draftStart.startOf('day'), day.endOf('day'));
        handleClose();
    };

    return (
        <>
            <ButtonBase
                onClick={handleOpen}
                aria-label="Selecionar período"
                aria-haspopup="dialog"
                aria-expanded={Boolean(anchorEl)}
                sx={{
                    height: 40,
                    px: '12px',
                    gap: '8px',
                    borderRadius: '9px',
                    border: '1px solid',
                    borderColor: anchorEl ? 'primary.main' : 'divider',
                    bgcolor: 'background.subtle',
                    color: 'text.primary',
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: 'primary.main' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
                }}
            >
                <CalendarDays size={16} strokeWidth={2} />
                {label}
            </ButtonBase>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px' } } }}
            >
                <Typography sx={{ px: 2, pt: 1.5, fontSize: '12px', color: 'text.secondary' }}>
                    {picking ? 'Selecione a data final' : 'Selecione a data inicial'}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    {dual ? (
                        <Box sx={{ px: 1, pb: 1 }} onMouseLeave={() => setHoverDay(null)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                                {[
                                    { id: 'left', month: leftMonth, move: moveLeft },
                                    { id: 'right', month: rightMonth, move: moveRight },
                                ].map(({ id, month, move }) => (
                                    <Box key={id} sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                        <IconButton size="small" aria-label={`Mês anterior (${id === 'left' ? 'início' : 'fim'})`} onClick={() => move(-1)}>
                                            <ChevronLeft size={18} />
                                        </IconButton>
                                        <Typography sx={monthTitleSx}>{month.locale('pt-br').format('MMMM [de] YYYY')}</Typography>
                                        <IconButton size="small" aria-label={`Próximo mês (${id === 'left' ? 'início' : 'fim'})`} onClick={() => move(1)}>
                                            <ChevronRight size={18} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex' }}>
                                {[leftMonth, rightMonth].map((month) => {
                                    return (
                                        <DateCalendar
                                            key={month.format('YYYY-MM')}
                                            value={null}
                                            referenceDate={month}
                                            onChange={handlePick}
                                            slots={{ day: RangeDay, calendarHeader: NoCalendarHeader }}
                                            slotProps={{ day: { rangeStart: draftStart, rangeEnd: draftEnd, hoverDay: picking ? hoverDay : null, onHover: setHoverDay } }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : (
                        <DateCalendar
                            value={null}
                            referenceDate={draftStart || dayjs()}
                            onChange={handlePick}
                            slots={{ day: RangeDay }}
                            slotProps={{ day: { rangeStart: draftStart, rangeEnd: draftEnd } }}
                        />
                    )}
                </LocalizationProvider>
                {clearable && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 1.5 }}>
                        <ButtonBase
                            onClick={handleClear}
                            disabled={!hasPeriod && !draftStart}
                            sx={{
                                height: 32,
                                px: '12px',
                                borderRadius: '9px',
                                border: '1px solid',
                                borderColor: 'divider',
                                color: 'text.secondary',
                                fontSize: '12px',
                                fontWeight: 700,
                                fontFamily: 'inherit',
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                                '&.Mui-disabled': { opacity: 0.5 },
                            }}
                        >
                            Limpar período
                        </ButtonBase>
                    </Box>
                )}
            </Popover>
        </>
    );
}

/**
 * Props aditivas (os dashboards não passam e mantêm o comportamento original):
 * - `showRefresh` (padrão true): `false` oculta o botão atualizar.
 * - `align` (padrão 'end'): `'start'` alinha à esquerda (sem `ml: auto`), usado
 *   na `ReportFilterBar` dos relatórios.
 * - `showShortcuts` (padrão true): `false` oculta os chips Hoje/7 dias/... e deixa
 *   só o campo único de período (telas com pouco espaço, ex.: Próximos serviços).
 * - `dualMonth` (padrão false): calendário com dois meses lado a lado (mês inicial e
 *   final) com navegação independente em cada painel; abaixo de 600px volta a um mês só.
 * - `clearable` (padrão false): mostra "Limpar período" no popover; ao limpar chama
 *   `onChange(null, null)` e o campo volta a "Selecionar período".
 */
export default function DesktopPeriodSelector({ start, end, onChange, onRefresh, loading, showRefresh = true, align = 'end', showShortcuts = true, dualMonth = false, clearable = false }) {
    const active = detectDesktopPeriod(start, end);
    return (
        <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: align === 'start' ? 'flex-start' : 'flex-end',
            gap: 1.5,
            ml: align === 'start' ? 0 : 'auto',
            maxWidth: '100%',
        }}>
            {showShortcuts && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {DESKTOP_PERIOD_OPTIONS.map((o) => (
                    <Chip
                        key={o.id}
                        label={o.label}
                        clickable
                        aria-pressed={o.id === active}
                        onClick={() => {
                            const r = desktopPeriodRange(o.id);
                            onChange(r.start, r.end);
                        }}
                        sx={periodChipSx(o.id === active)}
                    />
                ))}
            </Box>
            )}

            <PeriodRangeField start={start} end={end} onApply={onChange} dualMonth={dualMonth} clearable={clearable} />

            {showRefresh && (
            <Tooltip title="Atualizar dados">
                <span>
                    <IconButton
                        onClick={onRefresh}
                        disabled={loading}
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '10px',
                            border: '1px solid',
                            borderColor: 'divider',
                            color: 'text.secondary',
                            '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'background.subtle' },
                        }}
                    >
                        <RefreshCw size={16} strokeWidth={2} />
                    </IconButton>
                </span>
            </Tooltip>
            )}
        </Box>
    );
}
